import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Box, FormControl, styled } from "@mui/material";
import SupplierApi from "../../../api/ChemInv/Supplier";
import Tradename from "../../../api/ChemInv/Tradename";
import SupplierTradename from "../../../api/ChemInv/SupplierTradename.js";
import { GlobalSecondaryButton } from "../../styles";
import {
  StyledTableBody,
  StyledTableCell,
  StyledTableRow,
  UXDataTableWithoutBody,
} from "../../../components/UXDataTable";
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";
import ModalSimpleButton from "../../../components/Modal/ModalSimpleButton";
import CustomModal from "../../../components/Modal";
import SupplierTradeNameForm from "./SupplierTradeNameForm";
import SupplierAutocompleteField from "./SupplierAutocompleteField";
import TradeNameAutocompleteField from "./TradeNameAutocompleteField";
import { Supplier, TradeName } from "../../../api/types";

interface SupplierTradenameNormalizedAssociation {
  id?: number;
  supplierName: string;
  supplierNameLower: string;
  tradenameID: number;
  tradenameName: string;
  tradenameLower: string;
  chemID: string;
  substanceName: string;
  [key: string]: unknown;
}

interface ModalState {
  open: boolean;
  title: string;
  text: string;
  buttonText: string;
}

const Header = styled(Box)(() => ({
  fontSize: "20px",
  marginLeft: 10,
  paddingRight: 20,
}));

const StyledBox = styled(Box)(() => ({
  display: "flex",
  marginBottom: "20px",
}));

const colHeaders = [
  { id: "supplierName", label: "Supplier" },
  { id: "tradenameName", label: "Trade Name" },
  { id: "chemID", label: "Chemical" },
];

const SupplierTradeNameManagement: FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [tradenames, setTradenames] = useState<TradeName[]>([]);
  const [supplierTradenameAssociations, setSupplierTradenameAssociations] = useState<
    SupplierTradenameNormalizedAssociation[]
  >([]);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedTradename, setSelectedTradename] = useState<TradeName | null>(null);

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("supplierName");

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);

  const [tradeNameInputValue, setTradeNameInputValue] = useState("");

  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    title: "",
    text: "",
    buttonText: "Ok",
  });
  const isSavingRef = useRef(false);

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.STLCSME, Roles.Developer]);

  const associationIndex = useMemo(() => {
    const index = new Map<string, boolean>();
    (supplierTradenameAssociations ?? []).forEach((assoc) => {
      const key = `${assoc.supplierName}|${assoc.tradenameID}`;
      index.set(key, true);
    });
    return index;
  }, [supplierTradenameAssociations]);

  const filteredAssociations = useMemo(() => {
    if (!selectedSupplier && !selectedTradename) {
      return supplierTradenameAssociations ?? [];
    }

    return (supplierTradenameAssociations ?? []).filter((association) => {
      if (selectedSupplier && association.supplierName !== selectedSupplier.supplierName) {
        return false;
      }

      if (selectedTradename) {
        const selectedTradenameID = selectedTradename.id;
        if (selectedTradenameID === undefined || association.tradenameID !== selectedTradenameID) {
          return false;
        }
      }
      return true;
    });
  }, [supplierTradenameAssociations, selectedSupplier, selectedTradename]);

  const selectedAssociationAlreadyExists = useMemo(() => {
    if (!selectedSupplier || !selectedTradename) {
      return false;
    }

    const selectedTradenameID = selectedTradename.id;
    if (selectedTradenameID === undefined) {
      return false;
    }

    const key = `${selectedSupplier.supplierName}|${selectedTradenameID}`;
    return associationIndex.has(key);
  }, [selectedSupplier, selectedTradename, associationIndex]);

  useEffect(() => {
    const controller = new AbortController();
    loadAllData(controller.signal);
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(0);
  }, [selectedSupplier, selectedTradename]);

  const loadAllData = async (signal?: AbortSignal): Promise<void> => {
    setIsLoading(true);

    try {
      const [supplierResponse, tradenameResponse, associationResponse] = await Promise.all([
        SupplierApi.getAll(signal),
        Tradename.getAll(signal),
        SupplierTradename.getAll(signal),
      ]);

      const sortedSuppliers = [...(supplierResponse ?? [])].sort((a, b) =>
        (a.supplierName ?? "").localeCompare(b.supplierName ?? ""),
      );
      const sortedTradenames = [...(tradenameResponse ?? [])]
        .map((tradename) => ({
          ...tradename,
          tradenameName: tradename.tradenameName ?? "",
          tradenameNameLower: (tradename.tradenameName ?? "").toLowerCase(),
        }))
        .sort((a, b) => (a.tradenameName ?? "").localeCompare(b.tradenameName ?? ""));

      const normalizedAssociations: SupplierTradenameNormalizedAssociation[] =
        (associationResponse ?? [])
        .flatMap((association: any) => {
          const supplierName = association.supplierName ?? association.supplier?.supplierName ?? "";
          const tradenameID = association.tradenameID ?? association.tradename?.id;
          const tradenameName =
            association.tradename?.tradenameName ?? association.tradenameName ?? "";
          if (!supplierName || typeof tradenameID !== "number" || !tradenameName) {
            console.warn("Incomplete supplier-tradename association payload", association);
            return [];
          }

          return [{
            id: association.id,
            supplierName,
            supplierNameLower: supplierName.toLowerCase(),
            tradenameID,
            tradenameName,
            tradenameLower: tradenameName.toLowerCase(),
            chemID: association.tradename?.chemID ?? association.chemID ?? "",
            substanceName: association.tradename?.substanceName ?? association.substanceName ?? "",
          }];
        })
        .sort(
          (
            a: SupplierTradenameNormalizedAssociation,
            b: SupplierTradenameNormalizedAssociation,
          ) =>
            a.supplierName.localeCompare(b.supplierName) ||
            a.tradenameName.localeCompare(b.tradenameName),
        );

      setSuppliers(sortedSuppliers);
      setTradenames(sortedTradenames);
      setSupplierTradenameAssociations(normalizedAssociations);
    } catch (error: any) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        return;
      }
      openModalSimpleButton(
        "Fail",
        `Error loading Supplier Trade Names: ${error?.message ?? error}`,
        "Ok",
      );
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const handleRequestSort = (_event: React.MouseEvent, property: string): void => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAssignModal = (): void => {
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = (): void => {
    setShowAssignModal(false);
  };

  const handleAssignSave = async (values: any): Promise<void> => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      await SupplierTradename.create({
        supplierName: values.supplierName,
        tradenameID: values.tradenameID,
      });

      await loadAllData(undefined);
      setShowAssignModal(false);
      openModalSimpleButton("Success", "Supplier Trade Name saved successfully!", "Ok");
    } catch (error: any) {
      openModalSimpleButton(
        "Fail",
        `Error saving Supplier Trade Name: ${error?.message ?? error}`,
        "Ok",
      );
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const openModalSimpleButton = (title: string, text: string, buttonText: string): void => {
    setModalState({
      open: true,
      title,
      text,
      buttonText,
    });
  };

  const closeModalSimpleButton = (): void => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  const sortedAssociations = useMemo(() => {
    const copy = [...(filteredAssociations ?? [])];
    const isDesc = order === "desc";
    const field =
      orderBy === "tradenameName"
        ? "tradenameName"
        : orderBy === "chemID"
        ? "chemID"
        : "supplierName";

    copy.sort((a: SupplierTradenameNormalizedAssociation, b: SupplierTradenameNormalizedAssociation) => {
      const aVal = (a[field as keyof SupplierTradenameNormalizedAssociation] ?? "")
        .toString()
        .trim();
      const bVal = (b[field as keyof SupplierTradenameNormalizedAssociation] ?? "")
        .toString()
        .trim();

      let cmp = 0;
      if (field === "chemID") {
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        const aIsNumeric = aVal !== "" && !Number.isNaN(aNum);
        const bIsNumeric = bVal !== "" && !Number.isNaN(bNum);
        if (aIsNumeric && bIsNumeric) {
          cmp = aNum - bNum;
        } else {
          cmp = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: "base" });
        }
      } else {
        cmp = aVal.localeCompare(bVal, undefined, { sensitivity: "base" });
      }

      if (cmp === 0) {
        cmp =
          (a.supplierName ?? "").localeCompare(b.supplierName ?? "", undefined, {
            sensitivity: "base",
          }) ||
          (a.tradenameName ?? "").localeCompare(b.tradenameName ?? "", undefined, {
            sensitivity: "base",
          }) ||
          (a.id ?? "").toString().localeCompare((b.id ?? "").toString());
      }

      return isDesc ? -cmp : cmp;
    });

    return copy;
  }, [filteredAssociations, order, orderBy]);

  function determineStartArray(
    array: SupplierTradenameNormalizedAssociation[],
  ): SupplierTradenameNormalizedAssociation[] {
    if (!array || array.length === 0) {
      return [];
    }
    return array.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }

  return (
    <StyledBox flexDirection="column" width="100%" maxWidth="1200px" justifyContent="space-between">
      <Box>
        <FormControl sx={{ m: 1, width: "50rem", display: "flex" }} variant="outlined">
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <SupplierAutocompleteField
              id="supplier-filter-select"
              value={selectedSupplier}
              onChange={setSelectedSupplier}
              options={suppliers ?? []}
              isLoading={isLoading}
              width="25rem"
            />

            <TradeNameAutocompleteField
              id="tradename-filter-select"
              value={selectedTradename}
              inputValue={tradeNameInputValue}
              onChange={(value) => {
                setSelectedTradename(value);
                setTradeNameInputValue(value?.tradenameName ?? "");
              }}
              onInputChange={(value, reason) => {
                if (reason === "input" || reason === "clear" || reason === "reset") {
                  setTradeNameInputValue(value ?? "");
                }
              }}
              options={tradenames ?? []}
              isLoading={isLoading}
              width="20rem"
            />
          </StyledBox>
        </FormControl>
      </Box>

      <Box
        sx={{
          display: "inline-flex",
          marginBottom: "30px",
          marginTop: "20px",
          alignItems: "center",
        }}>
        <Header>Supplier Trade Names ({filteredAssociations.length})</Header>
        {canEdit && (
          <GlobalSecondaryButton
            variant="contained"
            type="button"
            onClick={handleOpenAssignModal}
            disabled={isLoading || isSaving || selectedAssociationAlreadyExists}>
            Assign Supplier Trade Name
          </GlobalSecondaryButton>
        )}
      </Box>

      <UXDataTableWithoutBody
        tableWidth="100%"
        cols={colHeaders}
        blankFirstHeader={false}
        tableId="supplierTradeNameTable"
        rowLength={filteredAssociations.length}
        enablePaging={true}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        noDataFoundMessage={"No supplier trade names found"}
        enableAddIcon={false}
        addFunction={null}
        addToolTipText={null}
        enableCheckbox={false}
        handleSelectAllClick={() => {}}
        enableSorteable={true}
        order={order}
        orderBy={orderBy}
        isDataLoading={isLoading}
        handleRequestSort={handleRequestSort}>
        <StyledTableBody key={"supplierTradeNameTableBody"}>
          {determineStartArray(sortedAssociations).map((association, rowIndex) => (
            <StyledTableRow
              hover
              key={`supplier-tradename-row-${
                association.id ??
                `${association.supplierName}-${association.tradenameID}-${rowIndex}`
              }`}>
              <StyledTableCell style={{ width: "250px" }}>
                {association.supplierName}
              </StyledTableCell>
              <StyledTableCell style={{ width: "250px" }}>
                {association.tradenameName}
              </StyledTableCell>
              <StyledTableCell style={{ width: "150px" }}>
                {association.chemID} - {association.substanceName}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </StyledTableBody>
      </UXDataTableWithoutBody>

      <CustomModal
        title="Assign Supplier Trade Name"
        open={showAssignModal}
        setOpen={setShowAssignModal}
        setClose={undefined}>
        <SupplierTradeNameForm
          suppliers={suppliers}
          tradenames={tradenames}
          existingAssociations={supplierTradenameAssociations}
          selectedSupplier={selectedSupplier}
          selectedTradename={selectedTradename}
          isSaving={isSaving}
          isLoading={isLoading}
          onSave={handleAssignSave}
          onCancel={handleCloseAssignModal}
        />
      </CustomModal>

      <ModalSimpleButton
        title={modalState.title}
        buttonText={modalState.buttonText}
        buttonAction={closeModalSimpleButton}
        open={modalState.open}
        setOpen={(open: boolean) => setModalState((prev) => ({ ...prev, open }))}>
        <label>{modalState.text}</label>
      </ModalSimpleButton>
    </StyledBox>
  );
};

export default SupplierTradeNameManagement;
